import { useEffect } from 'react';
import { Layout } from 'antd';
import { useMatch } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ErrorBoundary } from '@sentry/react';
import { SiderLayout } from '@refly/ai-workspace-common/components/sider/layout';
import { useBindCommands } from '@refly/ai-workspace-common/hooks/use-bind-commands';
import { useUserStoreShallow, useSiderStoreShallow } from '@refly/stores';
import { LOCALE } from '@refly/common-types';

import { LoginModal } from '../../components/login-modal';
import { SubscribeModal } from '@refly/ai-workspace-common/components/settings/subscribe-modal';
// [Voucher feature hidden] ClaimedVoucherPopup import removed - feature may be re-enabled in the future
import { VerificationModal } from '../../components/verification-modal';
import { ResetPasswordModal } from '../../components/reset-password-modal';
import { InvitationCodeModal } from '../../components/invitation-code-modal';
import { usePublicAccessPage } from '@refly/ai-workspace-common/hooks/use-is-share-page';
import { CanvasListModal } from '@refly/ai-workspace-common/components/workspace/canvas-list-modal';
import { ImportResourceModal } from '@refly/ai-workspace-common/components/import-resource';
import { BigSearchModal } from '@refly/ai-workspace-common/components/search/modal';
import { CanvasRenameModal } from '@refly/ai-workspace-common/components/canvas/modals/canvas-rename';
import { CanvasDeleteModal } from '@refly/ai-workspace-common/components/canvas/modals/canvas-delete';
import { DuplicateCanvasModal } from '@refly/ai-workspace-common/components/canvas/modals/duplicate-canvas-modal';
import { safeParseJSON } from '@refly/ai-workspace-common/utils/parse';

import { LightLoading } from '@refly/ui-kit';
import { isDesktop } from '@refly/ui-kit';
import { useGetUserSettings } from '@refly/ai-workspace-common/hooks/use-get-user-settings';
import { EnvironmentBanner } from './EnvironmentBanner';
import { useGetMediaModel } from '@refly/ai-workspace-common/hooks/use-get-media-model';
import { useHandleUrlParamsCallback } from '@refly/ai-workspace-common/hooks/use-handle-url-params-callback';
import { useRouteCollapse } from '@refly/ai-workspace-common/hooks/use-route-collapse';
import cn from 'classnames';
import { FormOnboardingModal } from '../form-onboarding-modal';
import { OnboardingSuccessModal } from '../onboarding-success-modal';
import { LibraryModal } from '@refly/ai-workspace-common/components/workspace/library-modal';

const Content = Layout.Content;

interface AppLayoutProps {
  children?: any;
}

export const AppLayout = (props: AppLayoutProps) => {
  const { showCanvasListModal, setShowCanvasListModal, showLibraryModal, setShowLibraryModal } =
    useSiderStoreShallow((state) => ({
      showCanvasListModal: state.showCanvasListModal,
      showLibraryModal: state.showLibraryModal,
      setShowCanvasListModal: state.setShowCanvasListModal,
      setShowLibraryModal: state.setShowLibraryModal,
    }));

  const isPublicAccessPage = usePublicAccessPage();
  const matchPricing = useMatch('/pricing');
  const matchApp = useMatch('/app/:appId');
  const matchMyAssetDetail = useMatch('/my-assets/:shareId');
  const isVideoEditor = !!useMatch('/video-editor');

  useBindCommands();

  const userStore = useUserStoreShallow((state) => ({
    isLogin: state.isLogin,
    userProfile: state.userProfile,
    localSettings: state.localSettings,
    isCheckingLoginStatus: state.isCheckingLoginStatus,
  }));

  const showSider =
    (isPublicAccessPage || ((userStore.isLogin || !!userStore.userProfile) && !matchPricing)) &&
    !matchApp &&
    !matchMyAssetDetail;

  // Get storage user profile
  const storageUserProfile = safeParseJSON(localStorage.getItem('refly-user-profile'));
  const notShowLoginBtn = storageUserProfile?.uid || userStore?.userProfile?.uid;

  // Get locale settings
  const storageLocalSettings = safeParseJSON(localStorage.getItem('refly-local-settings'));

  const locale = storageLocalSettings?.uiLocale || userStore?.localSettings?.uiLocale || LOCALE.EN;

  // Check user login status
  useGetUserSettings();

  useGetMediaModel();

  // Change locale if not matched
  const { i18n } = useTranslation();
  useEffect(() => {
    if (locale && i18n.isInitialized && i18n.languages?.[0] !== locale) {
      i18n.changeLanguage(locale);
    }
  }, [i18n, locale]);

  // Root path no longer redirects - shows HomeIndex component directly

  // Handle payment callback
  useHandleUrlParamsCallback();

  // Handle sidebar collapse based on route changes
  useRouteCollapse();

  const routeLogin = useMatch('/');
  const isPricing = useMatch('/pricing');
  const matchCanvas = useMatch('/canvas/:canvasId');
  const matchWorkflow = useMatch('/workflow/:workflowId');
  const isShareFile = useMatch('/share/file/:shareId');
  const isWorkflowEmpty = matchCanvas?.params?.canvasId === 'empty';
  const isWorkflow = (!!matchCanvas || !!matchWorkflow) && !isWorkflowEmpty;

  if (!isPublicAccessPage && !isPricing && !isDesktop()) {
    if (userStore.isCheckingLoginStatus === undefined || userStore.isCheckingLoginStatus) {
      return <LightLoading />;
    }

    if (!notShowLoginBtn && !routeLogin) {
      return <LightLoading />;
    }
  }

  return (
    <ErrorBoundary>
      <EnvironmentBanner />
      <Layout
        className="app-layout main w-full overflow-x-hidden"
        style={{
          height: 'var(--screen-height)',
          background:
            'linear-gradient( 0deg, #EAE9FF 0%, #ECEFF9 30.46%, #E6EBFA 59.11%, #EEEAFD 99.59%)',
        }}
      >
        {showSider ? <SiderLayout source="sider" /> : null}
        <Layout
          className={cn(
            'content-layout bg-transparent flex-grow overflow-y-auto overflow-x-hidden rounded-xl min-w-0 min-h-0 overscroll-contain',
            !isShareFile && !isVideoEditor && 'm-2',
            isWorkflow || isVideoEditor ? '' : 'shadow-refly-m',
          )}
          style={
            isShareFile || isVideoEditor ? {} : { height: 'calc(var(--screen-height) - 16px)' }
          }
        >
          <Content>{props.children}</Content>
        </Layout>
        <BigSearchModal />
        <LoginModal />
        <VerificationModal />
        <FormOnboardingModal />
        <OnboardingSuccessModal />
        <InvitationCodeModal />
        <ResetPasswordModal />
        <SubscribeModal />
        {/* [Voucher feature hidden] <ClaimedVoucherPopup /> removed */}
        <CanvasListModal visible={showCanvasListModal} setVisible={setShowCanvasListModal} />
        <LibraryModal visible={showLibraryModal} setVisible={setShowLibraryModal} />
        <ImportResourceModal />
        <CanvasRenameModal />
        <CanvasDeleteModal />
        <DuplicateCanvasModal />
      </Layout>
    </ErrorBoundary>
  );
};
